import { ComponentType, useState } from "react";
import classNames from "classnames";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { Divider } from "components/design/Divider";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import { ArrowDown } from "components/icons/ArrowDown";
import { Library } from "components/icons/Library";
import { BookAlt } from "components/icons/BookAlt";
import { ChartPieDollar } from "components/icons/ChartPieDollar";
import { SvgProps } from "components/icons/types";
import Laptop from "static/images/Laptop.svg";
import { BenefitCategory, TaxBenefit } from "types/Models/estimatedTax";
import { currency } from "utils/Currency";
import { BenefitBreakdownTable } from "./BenefitBreakdownTable";

type Props = {
  benefit: TaxBenefit;
  entityId: string;
  isExpanded: boolean;
  onAddNew?: () => void;
  onUploadReturn?: () => void;
};

const CATEGORY_ICON: Partial<Record<BenefitCategory, ComponentType<SvgProps>>> =
  {
    NOL: Library,
    RND_EXPENSES: BookAlt,
    TAX_OVERPAYMENTS: ChartPieDollar,
  };

const BUSINESS_ASSETS_CATEGORY = "BUSINESS_ASSETS" as const;

const BenefitCategoryIcon = ({ category }: { category: BenefitCategory }) => {
  const Icon = CATEGORY_ICON[category];

  return (
    <div className="t-flex t-h-[26px] t-w-[26px] t-shrink-0 t-items-center t-justify-center t-rounded-[3px] t-bg-purple-0 t-p-[6px] t-text-purple-40">
      {category === BUSINESS_ASSETS_CATEGORY ? (
        <img src={Laptop} alt="Laptop" className="t-size-4" />
      ) : (
        Icon && <Icon width={14} height={14} />
      )}
    </div>
  );
};

const sumBreakdown = (benefit: TaxBenefit) => {
  return benefit.breakdown.reduce(
    (totals, entry) => ({
      recorded: totals.recorded + (entry.original_amount ?? entry.amount ?? 0),
      used: totals.used + (entry.amount_used ?? 0),
      left: totals.left + (entry.amount_left ?? entry.amount ?? 0),
    }),
    { recorded: 0, used: 0, left: 0 }
  );
};

export const BenefitCategoryRow = ({
  benefit,
  entityId,
  isExpanded,
  onAddNew,
  onUploadReturn,
}: Props) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const hasEntries = benefit.breakdown.length > 0;
  const totals = sumBreakdown(benefit);

  return (
    <Accordion.RawItem value={benefit.category}>
      <Accordion.RawTrigger asChild>
        <button
          type="button"
          className="all:unset t-flex t-w-full t-cursor-pointer t-items-center t-gap-3"
        >
          <BenefitCategoryIcon category={benefit.category} />
          <div className="t-flex-1 t-text-left">
            <p className="t-m-0 t-text-subtext t-text-text-60">
              {benefit.title}
            </p>
            <p className="t-m-0 t-text-body t-font-light t-text-text-30">
              {benefit.description}
            </p>
          </div>
          <div className="t-flex t-items-center t-gap-2 t-shrink-0">
            {hasEntries ? (
              <span>
                <span className="t-text-subtext t-text-text-60">
                  {currency({
                    amount: benefit.amount_left,
                    maximumFractionDigits: 0,
                  })}
                </span>{" "}
                <span className="t-text-body-sm-lite t-text-text-30">
                  left to use
                </span>
              </span>
            ) : (
              <span className="t-text-body-sm t-italic t-text-text-30">
                Not added
              </span>
            )}
            <span
              className={classNames(
                "t-flex t-p-0.5 t-transition-transform t-duration-200",
                { "t-rotate-180": isExpanded, "t-rotate-0": !isExpanded }
              )}
            >
              <ArrowDown size="16" strokeWidth="1.5" />
            </span>
          </div>
        </button>
      </Accordion.RawTrigger>

      <Accordion.RawContent>
        <div className="t-mt-3 t-flex t-flex-col t-gap-3 t-pl-[38px]">
          <div className="t-flex t-items-center t-gap-2 t-text-body-sm-lite">
            <span className="t-text-text-30">Total recorded:</span>
            <span className="t-font-normal t-text-text-60">
              {currency({ amount: totals.recorded, maximumFractionDigits: 0 })}
            </span>
            <Divider
              orientation="vertical"
              color="dark-grey"
              classNames="t-h-4"
            />
            <span className="t-text-text-30">Used:</span>
            <span className="t-font-normal t-text-text-60">
              {currency({ amount: totals.used, maximumFractionDigits: 0 })}
            </span>
            <Divider
              orientation="vertical"
              color="dark-grey"
              classNames="t-h-4"
            />
            <span className="t-text-text-30">Left:</span>
            <span className="t-font-normal t-text-text-60">
              {currency({ amount: totals.left, maximumFractionDigits: 0 })}
            </span>
            {onAddNew && (
              <>
                <Divider
                  orientation="vertical"
                  color="dark-grey"
                  classNames="t-h-4"
                />
                <Button
                  customType="link"
                  size="small"
                  className="!t-font-normal"
                  onClick={onAddNew}
                >
                  <span className="t-flex t-items-center t-gap-1">
                    <SmallPlusIcon />
                    Add new
                  </span>
                </Button>
              </>
            )}
            {onUploadReturn && (
              <>
                <Divider
                  orientation="vertical"
                  color="dark-grey"
                  classNames="t-h-4"
                />
                <Button
                  customType="link"
                  size="small"
                  className="!t-font-normal"
                  onClick={onUploadReturn}
                >
                  Upload return
                </Button>
              </>
            )}
          </div>

          {hasEntries && (
            <Button
              customType="link_neutral"
              size="small"
              className="t-w-fit !t-font-normal"
              onClick={() => setShowBreakdown((v) => !v)}
            >
              <span className="t-flex t-items-center t-gap-1">
                {showBreakdown ? "Hide breakdown" : "Show breakdown"}
                <span
                  className={classNames(
                    "t-transition-transform t-duration-200 t-flex",
                    {
                      "t-rotate-180": showBreakdown,
                      "t-rotate-0": !showBreakdown,
                    }
                  )}
                >
                  <ArrowDown size="16" strokeWidth="1.2" color="currentColor" />
                </span>
              </span>
            </Button>
          )}

          {hasEntries && showBreakdown && (
            <BenefitBreakdownTable
              entries={benefit.breakdown}
              entityId={entityId}
              category={benefit.category}
            />
          )}
        </div>
      </Accordion.RawContent>
    </Accordion.RawItem>
  );
};
